// ============================================================================
// project-duplicate-guard.ts — save-time duplicate-project fork detection
// ----------------------------------------------------------------------------
// 2026-07-11 dedup investigation (memory: project-duplicate-projects-dedup):
// a save firing with currentProjectId === null mints a fresh UUID at
// App.jsx saveCurrentProjectImpl and regenerates the identical address-
// derived "QUOTE RBB/… (MMYY)" title — the backend POST upserts only by
// dexie_id/UUID, so every such save forks an exact-title twin row. The live
// DB accumulated 23 duplicate groups / 57 excess rows May–June 2026 (worst:
// 11 CSC-UNIT-12 records in 14 minutes from a 10-second autosave loop).
//
// This module holds the PURE pieces of the prevention: natural-key
// normalisation (title + quote number), candidate matching against the
// in-memory project summaries (which are API-backed in backend mode, so
// backend-only records are visible), and a session-scoped decision memory
// so the user is asked AT MOST ONCE per natural key — a repeatedly-lost
// currentProjectId must not re-prompt every 10 s, and once the user picks
// "create new anyway" the minted id is reused on re-fire instead of forking
// again. App.jsx owns the interactive confirm + the batch-mode bypass.
// ============================================================================

/**
 * Normalise one side of the natural key: trim, collapse internal whitespace,
 * lowercase. Returns '' for null/undefined/empty so both sides compare
 * uniformly ('' === '' counts as a match — an empty quote number on both
 * records is still "identical quote_number").
 */
export function normaliseDuplicateMatchValue(raw: unknown) {
    if (raw == null) return '';
    return String(raw).trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Build the natural key a save is about to write. Returns '' (= "do not
 * guard") when the title is empty or still the 'Untitled Project'
 * placeholder — untitled drafts legitimately coexist and must never
 * prompt or cross-link.
 */
export function buildDuplicateMatchKey({ title, quoteNumber }: { title?: unknown; quoteNumber?: unknown } = {}) {
    const titleKey = normaliseDuplicateMatchValue(title);
    if (!titleKey || titleKey === 'untitled project') return '';
    return `${titleKey}::${normaliseDuplicateMatchValue(quoteNumber)}`;
}

/**
 * Extract the same natural key from an existing project summary. Reads both
 * key casings because candidates arrive from either the Dexie summary
 * builder (quoteNumber) or apiFetch's camelCased backend rows (quoteNumber
 * with a quote_number alias — the alias is non-enumerable, so read the
 * canonical camelCase first).
 */
export function projectDuplicateMatchKey(project: any) {
    if (!project) return '';
    return buildDuplicateMatchKey({
        title: project.title,
        quoteNumber: project.quoteNumber ?? project.quote_number ?? '',
    });
}

/**
 * Find existing projects whose title + quote number equal what an id-less
 * save is about to create. Most recently updated first, so callers can
 * offer the freshest twin as the reuse target (historical duplicate groups
 * may hold several rows until the operator's cleanup pass runs).
 *
 * @param {Array<object>} projects - project summaries (Dexie or API shape)
 * @param {{title?: string, quoteNumber?: string}} candidate - the pending save
 * @returns {Array<object>} matches, newest updated_at first ([] when the
 *   candidate key is empty/untitled — never guard those)
 */
export function findDuplicateProjectCandidates(projects: any, candidate: { title?: unknown; quoteNumber?: unknown } = {}) {
    const key = buildDuplicateMatchKey(candidate);
    if (!key || !Array.isArray(projects)) return [];
    // v5.50.6: never offer an imported HISTORY archive as a "continue in the
    // existing project" target. 1,519 archive titles materially widen the
    // title+quote# collision surface for legitimate re-quotes of past jobs,
    // and answering 'reuse' would overwrite the archive record's content.
    // A re-quote of an archived job is ALWAYS a new project.
    const matches = projects.filter(p => p && p.id
        && (p.projectType || p.project_type) !== 'history'
        && projectDuplicateMatchKey(p) === key);
    return matches.sort((a, b) => {
        const ta = Date.parse(a.updated_at ?? a.updatedAt ?? '') || 0;
        const tb = Date.parse(b.updated_at ?? b.updatedAt ?? '') || 0;
        return tb - ta;
    });
}

// ─── Session-scoped decision memory ─────────────────────────────────────────
// Keyed by the natural key, valued { choice: 'reuse'|'new', projectId }.
// Module scope = lives for the browser session, cleared on reload (a reload
// restores currentProjectId via session-restore, so the memory is only
// needed while the id-loss bug is live within one session). Bounded — the
// key space is "distinct projects the user saved without an id this
// session", which is tiny; cap defensively anyway.
const _duplicateSaveDecisions = new Map<string, { choice: string; projectId: string }>();
const MAX_REMEMBERED_DECISIONS = 64;

/**
 * Record the user's (or batch default's) answer for a natural key. For
 * 'new', pass the freshly minted id — a later id-less save for the same key
 * reuses THAT id instead of minting a third fork.
 */
export function rememberDuplicateSaveDecision(key: string, { choice, projectId }: { choice: string; projectId: string }) {
    if (!key || !projectId) return;
    _duplicateSaveDecisions.set(key, { choice, projectId });
    if (_duplicateSaveDecisions.size > MAX_REMEMBERED_DECISIONS) {
        const oldest = _duplicateSaveDecisions.keys().next().value;
        if (oldest !== undefined) _duplicateSaveDecisions.delete(oldest);
    }
}

/** @returns {{choice: string, projectId: string}|null} */
export function recallDuplicateSaveDecision(key: string) {
    if (!key) return null;
    return _duplicateSaveDecisions.get(key) || null;
}

// Test seam.
export const _internals = {
    _duplicateSaveDecisions,
    clearDuplicateSaveDecisions() { _duplicateSaveDecisions.clear(); },
};
